<template>
  <div class="page-content">
    <div class="question-bank-page">
      <div class="page-header">
        <h2 class="page-title">题库管理</h2>
        <p class="page-subtitle">维护测谎答题题库，超级管理员及授权用户可增删改。</p>
      </div>

      <div class="toolbar-card">
        <input
          v-model="keyword"
          type="text"
          class="search-input"
          placeholder="搜索韩文 / 中文 / 答案 / 操作人..."
          @keyup.enter="loadList(1)"
        />
        <button class="btn secondary" @click="loadList(1)">搜索</button>
        <button class="btn secondary" :disabled="recognizing" @click="triggerRecognize">
          {{ recognizing ? '识别中...' : '识别图片' }}
        </button>
        <input
          ref="imageInputRef"
          type="file"
          accept="image/*"
          class="hidden-file-input"
          @change="handleRecognizeImage"
        />
        <button class="btn primary" @click="openCreate">新增题目</button>
      </div>

      <div class="table-card" v-loading="loading">
        <div class="table-header">
          <div class="header-cell ko-col">问题韩文</div>
          <div class="header-cell zh-col">问题中文</div>
          <div class="header-cell answer-col">答案</div>
          <div class="header-cell operator-col">操作人</div>
          <div class="header-cell time-col">更新时间</div>
          <div class="header-cell actions-col">操作</div>
        </div>

        <div v-if="rows.length === 0 && !loading" class="empty-state">
          <div class="empty-text">暂无题目</div>
          <div class="empty-desc">点击「新增题目」添加题库内容</div>
        </div>

        <div v-for="row in rows" :key="row.id" class="table-row">
          <div class="cell ko-col" :title="row.question_ko">{{ row.question_ko || '-' }}</div>
          <div class="cell zh-col" :title="row.question_zh">{{ row.question_zh || '-' }}</div>
          <div class="cell answer-col" :title="row.answer">{{ row.answer || '-' }}</div>
          <div class="cell operator-col" :title="row.operator_name">{{ row.operator_name || '-' }}</div>
          <div class="cell time-col">{{ formatDateTime(row.update_time) }}</div>
          <div class="cell actions-col">
            <button type="button" class="action-btn" @click="openEdit(row)">编辑</button>
            <button type="button" class="action-btn danger" @click="handleDelete(row)">删除</button>
          </div>
        </div>
      </div>

      <div v-if="total > 0" class="pagination-bar">
        <span>共 {{ total }} 条，第 {{ page }} / {{ totalPages }} 页</span>
        <div class="pagination-controls">
          <button class="btn secondary" :disabled="page <= 1" @click="loadList(page - 1)">上一页</button>
          <button class="btn secondary" :disabled="page >= totalPages" @click="loadList(page + 1)">下一页</button>
        </div>
      </div>

      <el-dialog
        v-model="recognizeDialogVisible"
        title="识别结果（可编辑后一并保存）"
        width="860px"
        destroy-on-close
        class="question-bank-dialog recognize-dialog"
      >
        <p class="recognize-hint">请核对并修改下方题目的韩文、中文和答案；不需要的题目可点击「移除」，确认后点击「保存全部」入库。</p>
        <div v-if="previewImageUrl" class="preview-wrap">
          <img :src="previewImageUrl" alt="识别图片" class="preview-image" />
        </div>
        <div class="recognized-edit-list">
          <div v-if="recognizedQuestions.length === 0" class="recognized-empty">
            暂无题目，请关闭后重新识别
          </div>
          <div
            v-for="(item, index) in recognizedQuestions"
            :key="index"
            class="recognized-edit-card"
          >
            <div class="recognized-card-header">
              <div class="recognized-index">题目 {{ index + 1 }}</div>
              <button
                type="button"
                class="recognized-remove-btn"
                title="移除此题"
                @click="removeRecognizedQuestion(index)"
              >
                移除
              </button>
            </div>
            <div class="recognized-edit-grid">
              <div class="form-field">
                <label class="form-label">问题韩文</label>
                <textarea
                  v-model="item.question_ko"
                  class="form-textarea"
                  rows="2"
                  placeholder="韩文题干"
                />
              </div>
              <div class="form-field">
                <label class="form-label">问题中文</label>
                <textarea
                  v-model="item.question_zh"
                  class="form-textarea"
                  rows="2"
                  placeholder="中文翻译"
                />
              </div>
              <div class="form-field">
                <label class="form-label">答案</label>
                <input
                  v-model="item.answer"
                  type="text"
                  class="form-input"
                  placeholder="请选择/填写正确答案"
                />
              </div>
            </div>
          </div>
        </div>
        <template #footer>
          <button type="button" class="btn secondary" @click="recognizeDialogVisible = false">取消</button>
          <button type="button" class="btn primary" :disabled="batchSaving || recognizedQuestions.length === 0" @click="handleBatchSaveRecognized">
            {{ batchSaving ? '保存中...' : '保存全部' }}
          </button>
        </template>
      </el-dialog>

      <el-dialog
        v-model="dialogVisible"
        :title="editingId ? '编辑题目' : '新增题目'"
        width="640px"
        destroy-on-close
        class="question-bank-dialog"
      >
        <div class="dialog-form">
          <div class="form-field">
            <label class="form-label">问题韩文 <span class="required">*</span></label>
            <textarea
              v-model="form.question_ko"
              class="form-textarea"
              rows="3"
              placeholder="请输入韩文问题"
            />
          </div>
          <div class="form-field">
            <label class="form-label">问题中文 <span class="required">*</span></label>
            <textarea
              v-model="form.question_zh"
              class="form-textarea"
              rows="3"
              placeholder="请输入中文问题"
            />
          </div>
          <div class="form-field">
            <label class="form-label">答案 <span class="required">*</span></label>
            <input
              v-model="form.answer"
              type="text"
              class="form-input"
              placeholder="请输入答案"
            />
          </div>
        </div>
        <template #footer>
          <button type="button" class="btn secondary" @click="dialogVisible = false">取消</button>
          <button type="button" class="btn primary" :disabled="saving" @click="handleSave">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import questionBankAPI from '../api/questionBank'
import { canManageQuestionBank, getStoredUser } from '../constants/questionBank'

export default {
  name: 'QuestionBankManagement',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving = ref(false)
    const rows = ref([])
    const total = ref(0)
    const page = ref(1)
    const pageSize = ref(20)
    const keyword = ref('')
    const dialogVisible = ref(false)
    const recognizeDialogVisible = ref(false)
    const recognizing = ref(false)
    const batchSaving = ref(false)
    const recognizedQuestions = ref([])
    const previewImageUrl = ref('')
    const imageInputRef = ref(null)
    const editingId = ref(null)
    const form = ref({
      question_ko: '',
      question_zh: '',
      answer: ''
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))

    const ensureAccess = () => {
      const user = getStoredUser()
      if (!canManageQuestionBank(user)) {
        ElMessage.error('无权限访问题库管理')
        router.replace(user.is_admin === 2 ? '/dashboard/users' : '/dashboard/windows')
        return false
      }
      return true
    }

    const formatDateTime = (value) => {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleString(undefined, { hour12: false })
    }

    const resetForm = () => {
      editingId.value = null
      form.value = {
        question_ko: '',
        question_zh: '',
        answer: ''
      }
    }

    const loadList = async (targetPage = page.value) => {
      if (!ensureAccess()) return
      loading.value = true
      try {
        const res = await questionBankAPI.list(targetPage, pageSize.value, keyword.value)
        if (res.code === 200) {
          rows.value = res.data?.list || []
          total.value = res.data?.total || 0
          page.value = res.data?.page || targetPage
        } else {
          ElMessage.error(res.message || '加载失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const openCreate = () => {
      resetForm()
      dialogVisible.value = true
    }

    const triggerRecognize = () => {
      imageInputRef.value?.click()
    }

    const revokePreviewUrl = () => {
      if (previewImageUrl.value) {
        URL.revokeObjectURL(previewImageUrl.value)
        previewImageUrl.value = ''
      }
    }

    const normalizeRecognizedRows = (list) => {
      const rows = (list || []).slice(0, 3).map((item) => ({
        question_ko: item.question_ko || '',
        question_zh: item.question_zh || '',
        answer: item.answer || ''
      }))
      while (rows.length < 3) {
        rows.push({ question_ko: '', question_zh: '', answer: '' })
      }
      return rows
    }

    const handleRecognizeImage = async (event) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      if (!ensureAccess()) return

      revokePreviewUrl()
      previewImageUrl.value = URL.createObjectURL(file)

      recognizing.value = true
      try {
        const res = await questionBankAPI.recognizeImage(file)
        if (res.code === 200) {
          recognizedQuestions.value = normalizeRecognizedRows(res.data)
          const filled = recognizedQuestions.value.filter((r) => r.question_ko?.trim()).length
          if (filled === 0) {
            ElMessage.warning('未识别到题目，可手动填写后保存')
          }
          recognizeDialogVisible.value = true
          ElMessage.success(filled > 0 ? `识别到 ${filled} 道题，请核对后保存` : '请手动填写题目')
        } else {
          ElMessage.error(res.message || '识别失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '识别失败')
      } finally {
        recognizing.value = false
      }
    }

    const removeRecognizedQuestion = (index) => {
      recognizedQuestions.value.splice(index, 1)
    }

    const handleBatchSaveRecognized = async () => {
      const toSave = []
      for (let i = 0; i < recognizedQuestions.value.length; i++) {
        const item = recognizedQuestions.value[i]
        const row = {
          question_ko: item.question_ko?.trim() || '',
          question_zh: item.question_zh?.trim() || '',
          answer: item.answer?.trim() || ''
        }
        const hasAny = row.question_ko || row.question_zh || row.answer
        if (!hasAny) continue
        if (!row.question_ko || !row.question_zh || !row.answer) {
          ElMessage.warning(`题目 ${i + 1}：韩文、中文、答案均需填写`)
          return
        }
        toSave.push(row)
      }

      if (toSave.length === 0) {
        ElMessage.warning('请至少填写一道题目')
        return
      }

      batchSaving.value = true
      try {
        const res = await questionBankAPI.batchSave(toSave)
        if (res.code === 200) {
          ElMessage.success(res.message || `成功保存 ${toSave.length} 道题`)
          recognizeDialogVisible.value = false
          await loadList(1)
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存失败')
      } finally {
        batchSaving.value = false
      }
    }

    const openEdit = (row) => {
      editingId.value = row.id
      form.value = {
        question_ko: row.question_ko || '',
        question_zh: row.question_zh || '',
        answer: row.answer || ''
      }
      dialogVisible.value = true
    }

    const handleSave = async () => {
      const questionKo = form.value.question_ko?.trim()
      const questionZh = form.value.question_zh?.trim()
      const answer = form.value.answer?.trim()
      if (!questionKo || !questionZh || !answer) {
        ElMessage.warning('请填写问题韩文、问题中文和答案')
        return
      }
      saving.value = true
      try {
        const payload = {
          question_ko: questionKo,
          question_zh: questionZh,
          answer
        }
        if (editingId.value) {
          payload.id = editingId.value
        }
        const res = await questionBankAPI.save(payload)
        if (res.code === 200) {
          ElMessage.success('保存成功')
          dialogVisible.value = false
          await loadList(editingId.value ? page.value : 1)
        } else {
          ElMessage.error(res.message || '保存失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除该题目吗？`, '删除确认', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        })
        const res = await questionBankAPI.delete(row.id)
        if (res.code === 200) {
          ElMessage.success('删除成功')
          if (rows.value.length === 1 && page.value > 1) {
            await loadList(page.value - 1)
          } else {
            await loadList(page.value)
          }
        } else {
          ElMessage.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e.response?.data?.message || e.message || '删除失败')
        }
      }
    }

    onMounted(() => {
      if (ensureAccess()) {
        loadList(1)
      }
    })

    onUnmounted(() => {
      revokePreviewUrl()
    })

    return {
      loading,
      saving,
      recognizing,
      batchSaving,
      rows,
      total,
      page,
      keyword,
      dialogVisible,
      recognizeDialogVisible,
      recognizedQuestions,
      previewImageUrl,
      imageInputRef,
      editingId,
      form,
      totalPages,
      formatDateTime,
      loadList,
      openCreate,
      triggerRecognize,
      handleRecognizeImage,
      handleBatchSaveRecognized,
      removeRecognizedQuestion,
      openEdit,
      handleSave,
      handleDelete
    }
  }
}
</script>

<style scoped>
.question-bank-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.page-subtitle {
  color: #86868b;
  font-size: 14px;
}

.toolbar-card,
.table-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e5ea;
  margin-bottom: 16px;
}

.toolbar-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  flex-wrap: wrap;
}

.hidden-file-input {
  display: none;
}

.recognize-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6e6e73;
}

.preview-wrap {
  margin-bottom: 12px;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 220px;
  border-radius: 10px;
  border: 1px solid #e5e5ea;
}

.recognized-edit-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 52vh;
  overflow-y: auto;
  padding-right: 4px;
}

.recognized-edit-card {
  border: 1px solid #e5e5ea;
  border-radius: 12px;
  background: #fafafa;
  padding: 14px;
}

.recognized-edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.recognized-edit-grid .form-field:last-child {
  grid-column: 1 / -1;
}

.recognized-index {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.recognized-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.recognized-remove-btn {
  border: none;
  background: transparent;
  color: #86868b;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
}

.recognized-remove-btn:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.recognized-empty {
  padding: 24px;
  text-align: center;
  color: #86868b;
  font-size: 14px;
  border: 1px dashed #d2d2d7;
  border-radius: 12px;
}

@media (max-width: 720px) {
  .recognized-edit-grid {
    grid-template-columns: 1fr;
  }
}

.search-input,
.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #d2d2d7;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #1d1d1f;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-textarea {
  resize: vertical;
  min-height: 88px;
}

.search-input:focus,
.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #475569;
  box-shadow: 0 0 0 2px rgba(71, 85, 105, 0.12);
}

.search-input::placeholder,
.form-input::placeholder,
.form-textarea::placeholder {
  color: #9ca3af;
}

.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
}

.required {
  color: #dc2626;
}

.question-bank-dialog :deep(.el-dialog__footer) {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
}

.search-input {
  flex: 1;
  min-width: 220px;
}

.btn {
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
}

.btn.primary {
  background: #475569;
  color: #fff;
}

.btn.secondary {
  background: #f5f5f7;
  color: #1d1d1f;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 1.1fr 1.1fr 0.7fr 90px 130px 100px;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
}

.table-header {
  border-bottom: 1px solid #e5e5ea;
  font-size: 13px;
  font-weight: 600;
  color: #6e6e73;
}

.table-row {
  border-bottom: 1px solid #f2f2f7;
  font-size: 14px;
}

.table-row:last-child {
  border-bottom: none;
}

.cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-btn {
  border: none;
  background: #f5f5f7;
  color: #1d1d1f;
  border-radius: 8px;
  padding: 6px 10px;
  margin-right: 6px;
  cursor: pointer;
  font-size: 12px;
}

.action-btn.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.empty-state {
  padding: 48px 16px;
  text-align: center;
}

.empty-text {
  font-size: 16px;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 13px;
  color: #86868b;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #6e6e73;
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

@media (max-width: 900px) {
  .table-header {
    display: none;
  }

  .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 16px;
  }

  .cell::before {
    content: attr(data-label);
    display: block;
    font-size: 12px;
    color: #86868b;
    margin-bottom: 4px;
  }

  .actions-col {
    display: flex;
    gap: 8px;
  }
}
</style>
